import { useEffect, useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import { serverGql } from "__generated__/serverGql";
import { StripePaymentStatus } from "__generated__/serverGql/graphql";
import serverClient from "lib/apollo/serverClient";
import { assertPresence } from "utils/assertions";
import { asyncVoid } from "lib/errors/asyncVoidHandler";
import { useUpdateBrandSubscriptionCache } from "./useRefreshSubscriptionStatus";

const processCheckoutMutation = serverGql(/* ServerGraphQL */ `
  mutation ProcessStripeCheckoutSuccess($brandId: UUID!, $sessionId: String!) {
    processStripeCheckoutSuccess(brandId: $brandId, sessionId: $sessionId) {
      paymentStatus
      brand {
        id
        accessPlanId
        isSubscriptionValid
        stripeSubscriptionId
        ...RefreshBrandSubscriptionFields
      }
    }
  }
`);

type Options = {
  brandId: UUID | null | undefined;
  onProcessed?: () => void;
  sessionId: string | null | undefined;
};

export default function useProcessStripeCheckoutSuccess({
  brandId,
  onProcessed,
  sessionId,
}: Options) {
  const processedSessionIdRef = useRef<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const updateBrandSubscriptionCache = useUpdateBrandSubscriptionCache();
  const [processSession, mutationResult] = useMutation(
    processCheckoutMutation,
    { client: serverClient },
  );
  const shouldProcess =
    !!brandId && !!sessionId && processedSessionIdRef.current !== sessionId;

  useEffect(() => {
    if (!shouldProcess) {
      return;
    }

    assertPresence(brandId, "Expected brand ID to process checkout session.");
    assertPresence(sessionId, "Expected session ID to process checkout.");

    processedSessionIdRef.current = sessionId;
    setIsProcessing(true);

    asyncVoid(
      processSession({ variables: { brandId, sessionId } })
        .then((result) => {
          const processResult = result.data?.processStripeCheckoutSuccess;
          assertPresence(processResult, "No checkout result returned.");
          updateBrandSubscriptionCache(processResult.brand);
          onProcessed?.();
          setIsProcessing(false);
        })
        .catch((reason: unknown) => {
          setIsProcessing(false);
          throw reason;
        }),
    );
  }, [
    brandId,
    onProcessed,
    processSession,
    sessionId,
    shouldProcess,
    updateBrandSubscriptionCache,
  ]);

  const paymentStatus =
    mutationResult.data?.processStripeCheckoutSuccess?.paymentStatus;
  const loading =
    (!!sessionId && !brandId) ||
    shouldProcess ||
    isProcessing ||
    mutationResult.loading;

  return {
    ...mutationResult,
    hasCheckoutSession: !!sessionId,
    hasResult: !!paymentStatus || !!mutationResult.error,
    isProcessing: loading,
    isSucceeded:
      paymentStatus === StripePaymentStatus.PAID ||
      paymentStatus === StripePaymentStatus.NO_PAYMENT_REQUIRED,
    loading,
    paymentStatus,
  };
}
